import * as Api from '@ViewModels';
import { css } from 'aphrodite';
import * as React from 'react';
import { getSocialMediaPlatformLogoUrl } from '../../../../models/utils/socialUtils';
import { SimpleTooltip } from '../../Tooltip';
import { INSTAGRAM_HANDLES_WARNING } from './InstagramHandlesWarning';
import { styleSheet } from './styles';

interface IProps {
	activePlatform: Api.SocialMediaType;
	/** Ties each tab to the field it switches, so the tab list is announced against the editor. */
	controlsId?: string;
	isPlatformEdited: (platform: Api.SocialMediaType) => boolean;
	onSelect: (platform: Api.SocialMediaType) => void;
	platformHasErrors: (platform: Api.SocialMediaType) => boolean;
	platformHasHandlesToConfirm: (platform: Api.SocialMediaType) => boolean;
	platforms: Api.SocialMediaType[];
}

const describeDot = (hasErrors: boolean, hasHandlesToConfirm: boolean, isEdited: boolean) => {
	if (hasErrors) {
		return 'This version can’t publish yet';
	}
	if (hasHandlesToConfirm) {
		return INSTAGRAM_HANDLES_WARNING;
	}
	return isEdited ? 'Edited — differs from the base post' : '';
};

export const PlatformVersionTabs: React.FC<IProps> = ({
	activePlatform,
	controlsId,
	isPlatformEdited,
	onSelect,
	platformHasErrors,
	platformHasHandlesToConfirm,
	platforms,
}) => {
	const tabRefs = React.useRef(new Map<Api.SocialMediaType, HTMLButtonElement>());

	const onKeyDown = (e: React.KeyboardEvent<HTMLButtonElement>) => {
		const step = e.key === 'ArrowRight' ? 1 : e.key === 'ArrowLeft' ? -1 : 0;
		if (!step && e.key !== 'Home' && e.key !== 'End') {
			return;
		}
		e.preventDefault();
		const current = platforms.indexOf(activePlatform);
		const nextIndex =
			e.key === 'Home'
				? 0
				: e.key === 'End'
					? platforms.length - 1
					: (current + step + platforms.length) % platforms.length;
		const next = platforms[nextIndex];
		onSelect(next);
		tabRefs.current.get(next)?.focus();
	};

	return (
		<div aria-label='Platform versions' className={css(styleSheet.tabBar)} role='tablist'>
			{platforms.map(platform => {
				const isActive = platform === activePlatform;
				const hasErrors = platformHasErrors(platform);
				const hasHandlesToConfirm = platformHasHandlesToConfirm(platform);
				const isEdited = isPlatformEdited(platform);
				const dotLabel = describeDot(hasErrors, hasHandlesToConfirm, isEdited);
				return (
					<button
						aria-controls={controlsId}
						aria-selected={isActive}
						className={css(
							styleSheet.tab,
							hasErrors && styleSheet.tabInvalid,
							isActive && styleSheet.tabSelected,
							isActive && hasErrors && styleSheet.tabSelectedInvalid
						)}
						key={platform}
						onClick={() => onSelect(platform)}
						onKeyDown={onKeyDown}
						ref={element => {
							if (element) {
								tabRefs.current.set(platform, element);
								return;
							}
							tabRefs.current.delete(platform);
						}}
						role='tab'
						tabIndex={isActive ? 0 : -1}
						type='button'
					>
						<img
							alt=''
							className={css(styleSheet.tabLogo, !isActive && styleSheet.tabLogoMuted)}
							src={getSocialMediaPlatformLogoUrl(platform)}
						/>
						<span>{platform}</span>
						{dotLabel ? (
							<>
								<SimpleTooltip title={dotLabel}>
									<span
										className={css(
											styleSheet.tabDot,
											hasHandlesToConfirm && styleSheet.tabDotNotice,
											hasErrors && styleSheet.tabDotInvalid
										)}
									/>
								</SimpleTooltip>
								{/* The dot is the only thing marking the tab, so its meaning joins the tab's own name. */}
								<span className={css(styleSheet.srOnly)}>{dotLabel}</span>
							</>
						) : null}
					</button>
				);
			})}
		</div>
	);
};
